import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import secureLocalStorage from "react-secure-storage"
import { ClientPaymentRequests } from "../api/app.service"
import { isLocalStorageAvailable } from "../utils/isLocalStorageAvailable"
import { useIntercom } from "react-use-intercom"

export interface User {
  organization: string | null
  IdToken: string
  RefreshToken: string
  about: any
  access_token: string
  activeProfile: "coach" | "member" | null
  activeProfileId: any
  avatar_url: string
  canRecordSession: boolean
  coachProfile: any
  createdAt: any
  displayName: string
  email: string
  emailVerified: boolean
  firstName: string
  hasAvatar: boolean
  id_token_expiration: any
  isCoach: boolean
  isSubscribed: boolean
  lastName: string
  memberProfile: any
  phone: string
  phoneVerified: boolean
  promoCode: string | null
  region: any
  showWelcomeVideo: boolean
  stream_user_id: string
  stream_user_token: string
  timezone: any
  trialExpiration: any
  userColor: string
  user_id: string
  verifyEmailBy: any
  cohort_status: string
  referral_id: string
}

interface UserDataInterface {
  user: User
  setUser: React.Dispatch<React.SetStateAction<User>>
  clearUser: () => void
  hasOverduePayment: boolean
  setHasOverduePayment: React.Dispatch<React.SetStateAction<boolean>>
}

const initialUserData = {
  IdToken: "",
  RefreshToken: "",
  about: {},
  access_token: "",
  activeProfile: null,
  activeProfileId: null,
  avatar_url: "",
  canRecordSession: false,
  coachProfile: {},
  createdAt: null,
  displayName: "",
  email: "",
  emailVerified: false,
  firstName: "",
  hasAvatar: false,
  id_token_expiration: null,
  isCoach: false,
  isSubscribed: false,
  lastName: "",
  memberProfile: {},
  phone: "",
  phoneVerified: false,
  promoCode: null,
  region: {},
  showWelcomeVideo: false,
  stream_user_id: "",
  stream_user_token: "",
  timezone: "",
  trialExpiration: null,
  userColor: "",
  user_id: "",
  verifyEmailBy: null,
  organization: null,
  cohort_status: "",
  referral_id: "",
}

export const UserCtx = React.createContext<UserDataInterface>({
  user: initialUserData,
  setUser: () => {},
  clearUser: () => {},
  hasOverduePayment: false,
  setHasOverduePayment: () => {},
})

interface Props {
  children: React.ReactNode
}

export const UserContextProvider: React.FC<Props> = ({ children }) => {
  const { update, shutdown } = useIntercom()

  let userToSet = null
  if (isLocalStorageAvailable()) {
    const rawUser = secureLocalStorage.getItem("user")
    if (typeof rawUser === "string") {
      try {
        const parsedUser = JSON.parse(rawUser)
        userToSet =
          parsedUser && typeof parsedUser === "object" ? parsedUser : null
      } catch {
        userToSet = null
      }
    }
  }

  const [user, setUserState] = useState<User>(userToSet)
  const [hasOverduePayment, setHasOverduePayment] = useState(false)

  const setUser = (newUser: any) => {
    setUserState(newUser)

    const name =
      newUser.firstName && newUser.lastName
        ? newUser.firstName + " " + newUser.lastName
        : newUser.displayName || newUser.email
    update({
      email: newUser.email.toLowerCase(),
      userId: newUser.user_id,
      name,
      customAttributes: {
        user_type: newUser.activeProfile,
      },
      avatar: {
        type: "avatar",
        imageUrl: newUser.avatar_url,
      },
    })
  }

  useEffect(() => {
    const checkOverduePayments = async () => {
      if (!user?.user_id) return

      try {
        const res = await ClientPaymentRequests.getCoachPayments({
          user_id: parseInt(user.user_id),
        })

        const allTransactions = [
          ...(res?.subx_payments_list || []),
          ...(res?.coach_purchase_list || []),
        ]

        const now = dayjs()

        const overdue = allTransactions.some((t: any) => {
          const amount = parseFloat(t.amount || 0)
          const status =
            t.status?.toLowerCase?.() || t.payment_status?.toLowerCase?.() || ""
          const scheduledDate = dayjs(t.date_scheduled)

          const isDeclined = status === "declined" || status === "denied"

          const isScheduledOverdue =
            status === "scheduled" &&
            amount > 0 &&
            scheduledDate.isBefore(now, "day")

          return isDeclined || isScheduledOverdue
        })

        setHasOverduePayment(!!overdue)
      } catch (err) {
        console.error("Failed to check overdue payments:", err)
      }
    }

    checkOverduePayments()
  }, [user?.user_id])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "user") {
        const updatedUser = event.newValue
          ? JSON.parse(event.newValue)
          : initialUserData
        setUserState(updatedUser)
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [])

  const clearUser = () => {
    setUser(initialUserData)
    localStorage.removeItem("subscriptionPaymentModalShown")
    shutdown()
  }

  return (
    <UserCtx.Provider
      value={{
        user,
        setUser,
        clearUser,
        hasOverduePayment,
        setHasOverduePayment,
      }}
    >
      {children}
    </UserCtx.Provider>
  )
}
